import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { User } from 'types';
import { clearAll, saveUser } from 'utils';

import { types, AuthState } from './types';
import {
  login,
  logout,
  signUp,
  verifyCodes,
  checkEmailVerification,
} from './thunks';

const initialState: AuthState = {
  user: undefined,
  error: undefined,
  isLoggedIn: false,
  isGuestUser: false,
};

const reset = (state: AuthState) => {
  state.user = undefined;
  state.isLoggedIn = false;
  state.isGuestUser = false;
};

const updateUserInfo = (
  state: AuthState,
  action: PayloadAction<{ data: any; isLoggedIn: boolean }>
) => {
  const { data } = action.payload ?? {};
  const { user } = data;

  state.user = user;
  state.isGuestUser = !!user?.guestId;
  state.isLoggedIn = user !== undefined;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      reset(state);
    },
    updateAuthUser: (state, { payload }) => {
      const updatedUser: User = { ...state.user, ...payload };

      saveUser(updatedUser);

      return {
        ...state,
        user: updatedUser,
        isLoggedIn: !!updatedUser,
        isGuestUser: !!updatedUser?.guestId,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, updateUserInfo);
    builder.addCase(login.rejected, (state) => {
      reset(state);

      clearAll();
    });

    builder.addCase(logout.fulfilled, (state) => {
      reset(state);
    });
    builder.addCase(logout.rejected, (state) => {
      reset(state);
    });
    builder.addCase(signUp.fulfilled, updateUserInfo);
    builder.addCase(verifyCodes.fulfilled, updateUserInfo);
    builder.addCase(checkEmailVerification.fulfilled, (state, action) => {
      const { user, isLoggedIn } = action.payload;
      if (isLoggedIn && user) {
        state.user = user;
        state.isGuestUser = !!user?.guestId;
        state.isLoggedIn = user !== undefined;
      }
    });
  },
});

function isLogoutAction(action: AnyAction) {
  return (
    action.type === types.logout + '/fulfilled' ||
    action.type === types.logout + '/rejected'
  );
}

const {
  reducer: authReducer,
  actions: { clearAuth, updateAuthUser },
} = authSlice;

export {
  login,
  types,
  signUp,
  logout,
  clearAuth,
  authReducer,
  isLogoutAction,
  updateAuthUser,
};

export * from './selectors';
